import React from 'react';
import { Calendar, ArrowRight, Clock, CheckCircle, XCircle, AlertTriangle, Briefcase } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatDate } from '../../../../../utils/calculations/date';
import { pluralize } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета даты
 */
const ResultsOverview = ({ results, formData, timeUnits }) => {
  if (!results) return null;

  // Получаем правильное отображение единицы измерения
  const getUnitDisplay = (count, unitId) => {
    const unit = timeUnits.find(u => u.id === unitId);
    if (!unit) return '';
    
    if (count === 1) {
      return unit.name.toLowerCase();
    }
    
    if (count >= 2 && count <= 4) {
      return unit.namePlural.toLowerCase();
    }
    
    return unit.nameGenitive.toLowerCase();
  };

  // Функция получения класса для типа дня (выходной, праздник)
  const getDayTypeClass = (isWeekend, isHoliday) => {
    if (isHoliday) return 'text-red-600 dark:text-red-400 font-medium';
    if (isWeekend) return 'text-orange-600 dark:text-orange-400';
    return 'text-green-600 dark:text-green-400';
  };

  // Функция получения текста для типа дня
  const getDayTypeText = (isWeekend, isHoliday) => {
    if (isHoliday) return 'праздничный день';
    if (isWeekend) return 'выходной день';
    return 'рабочий день';
  };

  return (
    <ToolResult title="Результат расчета даты">
      {/* Основной результат */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-5 rounded-lg border border-cyan-100 dark:border-cyan-800">
        <div className="flex flex-col">
          <span className="text-sm text-cyan-600 dark:text-cyan-400">Начальная дата</span>
          <span className="text-xl font-bold text-gray-900 dark:text-white mt-1">{results.startFormatted}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">
            {results.startWeekday}
          </span>
        </div>
        
        <div className="flex items-center justify-center px-4">
          <div className="flex flex-col items-center">
            <ArrowRight className="w-6 h-6 text-cyan-500" />
            <span className="text-xs text-cyan-600 dark:text-cyan-400 mt-1 text-center whitespace-nowrap">
              {results.operation.mode === 'add' ? '+' : '-'} {results.operation.value} {getUnitDisplay(results.operation.value, results.operation.unit)}
            </span>
          </div>
        </div>
        
        <div className="flex flex-col">
          <span className="text-sm text-cyan-600 dark:text-cyan-400">Результат</span>
          <span className="text-xl font-bold text-gray-900 dark:text-white mt-1">{results.resultFormatted}</span>
          <span className={`text-sm capitalize ${getDayTypeClass(results.isWeekend, results.isHoliday)}`}>
            {results.resultWeekday}, {getDayTypeText(results.isWeekend, results.isHoliday)}
          </span>
        </div>
      </div>

      {/* Дополнительная информация */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Начальная дата" 
          value={results.startFormatted} 
          icon={<Calendar className="w-4 h-4" />}
        />
        
        <ResultItem 
          label="Операция" 
          value={`${results.operation.mode === 'add' ? 'Добавить' : 'Вычесть'} ${results.operation.value} ${getUnitDisplay(results.operation.value, results.operation.unit)}`} 
          color="cyan"
        />
        
        <ResultItem 
          label="Результат" 
          value={results.resultFormatted} 
          color="cyan" 
          icon={<Calendar className="w-4 h-4" />}
        />
        
        <ResultItem 
          label="День недели результата" 
          value={results.resultWeekday} 
          icon={<Clock className="w-4 h-4" />} 
        />
        
        <ResultItem 
          label="Тип дня результата" 
          value={getDayTypeText(results.isWeekend, results.isHoliday)}
          color={results.isHoliday ? "red" : (results.isWeekend ? "orange" : "green")}
          icon={results.isHoliday || results.isWeekend ? <XCircle className="w-4 h-4"/> : <CheckCircle className="w-4 h-4"/>}
        />
        
        
        {formData.includeWorkingDays || results.operation.unit === 'workingDays' ? (
          <ResultItem 
            label="Рабочие дни" 
            value="Расчет произведен только по рабочим дням" 
            color="blue"
            icon={<Briefcase className="w-4 h-4" />}
          />
        ) : null}
        
        {formData.includeHolidays ? (
          <ResultItem 
            label="Праздничные дни" 
            value="Учитывались праздничные дни" 
            color="red"
            icon={<Calendar className="w-4 h-4" />}
          />
        ) : null}
      </div>

      {/* Предупреждения при необходимости */}
      {(results.isWeekend || results.isHoliday) && !formData.includeWorkingDays && results.operation.unit !== 'workingDays' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mt-2 border border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Полученная дата - {results.isHoliday ? 'праздничный день' : 'выходной'}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Обратите внимание, что результат расчета приходится на {results.isHoliday ? 'праздничный день' : 'выходной'}. 
                Если вам нужно получить только рабочий день, включите опцию "Учитывать только рабочие дни".
              </p>
            </div>
          </div>
        </div>
      )}
    </ToolResult>
  );
};

export default ResultsOverview;